import { Suspense, lazy, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Layout from './components/Layout.jsx';
import { useSession } from './SessionContext.jsx';

/**
 * One route per legacy .aspx page, keeping the original page names so existing
 * bookmarks, the DB-driven menu (FSWeb_MenuItem.Form) and staff muscle memory
 * all keep working: /FSWeb/SearchInvoices.aspx -> /SearchInvoices
 */
const pages = {
  // General
  Main: lazy(() => import('./pages/Main.jsx')),
  ClientProfile: lazy(() => import('./pages/ClientProfile.jsx')),
  PayerProfile: lazy(() => import('./pages/PayerProfile.jsx')),
  PayerProfileStat: lazy(() => import('./pages/PayerProfileStat.jsx')),
  PayersList: lazy(() => import('./pages/PayersList.jsx')),
  CarrierProfile: lazy(() => import('./pages/CarrierProfile.jsx')),
  GeneralNotes: lazy(() => import('./pages/GeneralNotes.jsx')),
  Forms: lazy(() => import('./pages/Forms.jsx')),
  SubstituteUser: lazy(() => import('./pages/SubstituteUser.jsx')),
  ChangePassword: lazy(() => import('./pages/ChangePassword.jsx')),

  // Day to day
  PaidToday: lazy(() => import('./pages/PaidToday.jsx')),
  PurchaseToday: lazy(() => import('./pages/PurchaseToday.jsx')),
  ChequesToday: lazy(() => import('./pages/ChequesToday.jsx')),

  // Invoices
  SearchBatches: lazy(() => import('./pages/SearchBatches.jsx')),
  SearchInvoices: lazy(() => import('./pages/SearchInvoices.jsx')),
  SearchCheques: lazy(() => import('./pages/SearchCheques.jsx')),
  Aging: lazy(() => import('./pages/Aging.jsx')),
  LossesRecovered: lazy(() => import('./pages/LossesRecovered.jsx')),
  ScanVerification: lazy(() => import('./pages/ScanVerification.jsx')),
  CarrierInvoices: lazy(() => import('./pages/CarrierInvoices.jsx')),
  InvoiceInformation: lazy(() => import('./pages/InvoiceInformation.jsx')),
  BatchInvoices: lazy(() => import('./pages/BatchInvoices.jsx')),

  // Financial
  ClientBalanceStatement: lazy(() => import('./pages/ClientBalanceStatement.jsx')),
  PayerBalanceStatement: lazy(() => import('./pages/PayerBalanceStatement.jsx')),
  ExecutiveReports: lazy(() => import('./pages/ExecutiveReports.jsx')),
  ReserveStatements: lazy(() => import('./pages/ReserveStatements.jsx')),
  CompanyRevenue: lazy(() => import('./pages/CompanyRevenue.jsx')),
  PromissoryNotes: lazy(() => import('./pages/PromissoryNotes.jsx')),
  CashRsvStatementRpt: lazy(() => import('./pages/CashRsvStatementRpt.jsx')),

  // Administrator
  UserProfiles: lazy(() => import('./pages/UserProfiles.jsx')),
  ClientsLegalDocs: lazy(() => import('./pages/ClientsLegalDocs.jsx')),
  FormsAndGroups: lazy(() => import('./pages/FormsAndGroups.jsx')),
  UsersPerformance: lazy(() => import('./pages/UsersPerformance.jsx')),
  UsersPerformanceDetail: lazy(() => import('./pages/UsersPerformanceDetail.jsx')),
  SiteMonitoring: lazy(() => import('./pages/SiteMonitoring.jsx')),
  EmployeeDays: lazy(() => import('./pages/EmployeeDays.jsx')),
  CheckFraudProtection: lazy(() => import('./pages/CheckFraudProtection.jsx')),
};

function NotFound() {
  const { pathname } = useLocation();
  return (
    <div className="page">
      <div className="msg warn">
        <strong>No such page.</strong>
        <div>
          {pathname} does not correspond to an FSWeb screen. Use the menu above, or return to the{' '}
          <a href="/Main">main page</a>.
        </div>
      </div>
    </div>
  );
}

function NotPermitted({ page }) {
  return (
    <div className="page">
      <div className="msg warn">
        <strong>Not available to your account.</strong>
        <div>
          Your FSWeb group does not include <strong>{page}</strong>. The menu above shows everything you can reach; an
          administrator can change this in User Profiles.
        </div>
      </div>
    </div>
  );
}

/**
 * Pages a few screens link to but which are not menu items in their own right.
 * The server still applies the real check on every request - this only keeps
 * the UI honest about what the menu grants.
 */
const DRILL_DOWN = {
  InvoiceInformation: [
    'SearchInvoices', 'PaidToday', 'PurchaseToday', 'ChequesToday',
    'SearchCheques', 'CarrierInvoices', 'LossesRecovered', 'ScanVerification',
    'PayerProfile', 'BatchInvoices',
  ],
  BatchInvoices: ['SearchBatches', 'ScanVerification'],
  UsersPerformanceDetail: ['UsersPerformance'],
};

/**
 * Landing page. Main is the usual home, but a group that does not grant it
 * (some client accounts) should still land somewhere useful rather than on a
 * permission error, so fall back to the first item their menu does grant.
 */
function Home() {
  const { user } = useSession();
  const target = useMemo(() => {
    const items = (user?.menu || []).flatMap((g) => g.items).filter((i) => i.path);
    if (user?.isAdministrator) return '/Main';
    const main = items.find((i) => i.path === '/Main');
    return main ? '/Main' : items[0]?.path || null;
  }, [user]);

  if (!target) {
    return (
      <div className="page">
        <div className="msg warn">
          <strong>Your account has no menu items.</strong>
          <div>An administrator needs to assign your FSWeb group in User Profiles.</div>
        </div>
      </div>
    );
  }
  return <Navigate to={target} replace />;
}

function Guarded({ page, children }) {
  const { user } = useSession();
  const granted = useMemo(() => {
    const set = new Set();
    for (const g of user?.menu || []) {
      for (const i of g.items) if (i.path) set.add(i.path.replace(/^\//, '').toLowerCase());
    }
    return set;
  }, [user]);

  if (user?.isAdministrator) return children;
  if (granted.has(page.toLowerCase())) return children;
  const parents = DRILL_DOWN[page];
  if (parents && parents.some((p) => granted.has(p.toLowerCase()))) return children;
  return <NotPermitted page={page} />;
}

export default function App() {
  return (
    <Layout>
      <Suspense
        fallback={
          <div className="page">
            <div className="loading-row"><span className="spinner" /> Loading page...</div>
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/default" element={<Home />} />
          {Object.entries(pages).map(([name, Component]) => (
            <Route
              key={name}
              path={`/${name}`}
              element={
                <Guarded page={name}>
                  <Component />
                </Guarded>
              }
            />
          ))}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}
